import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Avatar,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Card,
  CardBody,
  Flex,
  IconButton,
  useColorModeValue,
  Divider,
  Stack,
  Button,
  Collapse,
  useDisclosure,
  SimpleGrid,
  Center,
  Spinner,
} from "@chakra-ui/react";
import {
  FiPackage,
  FiTool,
  FiSettings,
  FiZap,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiMapPin,
  FiHash,
  FiInfo,
} from "react-icons/fi";
import { useRevalidator } from "react-router-dom";
import Link from "../../../../components/Link";
import urlToId from "../../../../helpers/UrlToId";
import { ServiceState } from "../../../../components/States";
import { translateType } from "../../../../shared/libs/translate";

// Mobile Service Record Card Component
function MobileServiceRecordCard({ service }) {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="lg"
      shadow="sm"
      _hover={{
        shadow: "md",
        transform: "translateY(-2px)",
        borderColor: "blue.300",
      }}
      transition="all 0.2s ease"
      mb={{ base: 2, sm: 3 }}
      mx={{ base: 0, sm: 0 }}
      maxW="100%"
    >
      <CardBody p={{ base: 3, sm: 4 }}>
        <VStack align="stretch" spacing={{ base: 2, sm: 3 }}>
          {/* Header with Service Number and Type */}
          <HStack justify="space-between" align="center" spacing={{ base: 2, sm: 3 }}>
            <HStack spacing={2} flex={1} minW={0}>
              <Icon as={FiHash} color="blue.500" boxSize={4} />
              <Badge
                colorScheme="purple"
                variant="outline"
                borderRadius="full"
                fontSize={{ base: "2xs", sm: "xs" }}
                px={{ base: 1, sm: 2 }}
                py={1}
              >
                {service.number}
              </Badge>
            </HStack>
            <Box flexShrink={0}>
              <ServiceState state={service.state} />
            </Box>
          </HStack>

          {/* Product Model */}
          <Link to={`/servis/${urlToId(service.url) || ""}`}>
            <HStack spacing={{ base: 2, sm: 3 }} align="center">
              <Icon as={FiPackage} color="blue.500" boxSize={5} />
              <VStack align="start" spacing={0} flex={1} minW={0}>
                <Text
                  fontWeight="semibold"
                  color="blue.500"
                  fontSize={{ base: "sm", sm: "md" }}
                  _hover={{ color: "blue.600" }}
                  transition="color 0.2s ease"
                  noOfLines={2}
                >
                  {service.product_model.name}
                </Text>
                <Badge
                  colorScheme="green"
                  variant="subtle"
                  borderRadius="md"
                  fontSize={{ base: "2xs", sm: "xs" }}
                >
                  {translateType(service.service_type)}
                </Badge>
              </VStack>
            </HStack>
          </Link>

          {/* Description */}
          {service.desc && (
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={FiInfo} color="gray.500" boxSize={4} />
                <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="medium" color={textColor}>
                  Açıklama
                </Text>
              </HStack>
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                color={subtitleColor}
                pl={{ base: 5, sm: 6 }}
                lineHeight="1.4"
                noOfLines={3}
              >
                {service.desc}
              </Text>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}

// Mobile Unit Card Component
function MobileUnitCard({ unit }) {
  const { isOpen, onToggle } = useDisclosure();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  const serviceCount = unit.service_records ? unit.service_records.length : 0;

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="xl"
      shadow="md"
      mb={{ base: 3, sm: 4 }}
      overflow="hidden"
      mx={{ base: 1, sm: 0 }}
      maxW="100%"
    >
      {/* Unit Header */}
      <Box bg={headerBg} p={{ base: 3, sm: 4 }}>
        <HStack justify="space-between" align="center" spacing={{ base: 2, sm: 3 }}>
          <HStack spacing={{ base: 2, sm: 3 }} flex={1} minW={0}>
            <Avatar
              size={{ base: "sm", sm: "md" }}
              name={unit.project_name}
              bg="teal.500"
              color="white"
              icon={<FiPackage />}
            />
            <VStack align="start" spacing={1} flex={1} minW={0}>
              <Text 
                fontWeight="bold" 
                color={textColor} 
                fontSize={{ base: "md", sm: "lg" }}
                noOfLines={1}
              >
                {unit.project_name}
              </Text>
              <HStack spacing={2}>
                <Icon as={FiMapPin} color="gray.500" boxSize={3} />
                <Text fontSize={{ base: "xs", sm: "sm" }} color={subtitleColor} noOfLines={1}>
                  Ünite: {unit.apt_num}
                </Text>
              </HStack>
            </VStack>
          </HStack>
          <VStack spacing={1} align="end" flexShrink={0}>
            <Badge
              colorScheme={serviceCount > 0 ? "orange" : "green"}
              variant="solid"
              borderRadius="full"
              px={{ base: 2, sm: 3 }}
              py={1}
              fontSize={{ base: "xs", sm: "sm" }}
            >
              {serviceCount} Servis
            </Badge>
          </VStack>
        </HStack>
      </Box>

      {/* Service Records Toggle Button */}
      {serviceCount > 0 && (
        <Box p={{ base: 3, sm: 4 }} pt={0}>
          <Button
            onClick={onToggle}
            variant="ghost"
            width="full"
            justifyContent="space-between"
            rightIcon={
              <Icon as={isOpen ? FiChevronUp : FiChevronDown} boxSize={4} />
            }
            size={{ base: "xs", sm: "sm" }}
            mt={2}
          >
            <HStack spacing={2}>
              <Icon as={FiTool} color="orange.500" boxSize={4} />
              <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="medium">
                {serviceCount} Adet Arızalı Ürün
              </Text>
            </HStack>
          </Button>

          {/* Service Records List */}
          <Collapse in={isOpen} animateOpacity>
            <Box mt={3} pt={3} borderTop="1px" borderColor={borderColor}>
              <VStack spacing={{ base: 2, sm: 2 }} align="stretch">
                {unit.service_records.map((service) => (
                  <MobileServiceRecordCard key={service.url} service={service} />
                ))}
              </VStack>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Empty State for Unit with No Services */}
      {serviceCount === 0 && (
        <Box p={{ base: 3, sm: 4 }} pt={0}>
          <Center py={4}>
            <VStack spacing={2}>
              <Icon as={FiTool} boxSize={8} color="gray.300" />
              <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.400" textAlign="center">
                Bu ünitede arızalı ürün bulunmuyor
              </Text>
            </VStack>
          </Center>
        </Box>
      )}
    </Card>
  );
}

// Main Mobile Service List Component
export default function ServiceListMobile({ services }) {
  const revalidator = useRevalidator();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const bgGradient = useColorModeValue(
    "linear(135deg, blue.50 0%, teal.50 100%)",
    "linear(135deg, gray.900 0%, gray.800 100%)"
  );
  const headerBg = useColorModeValue(
    "linear(135deg, teal.500 0%, green.600 100%)",
    "linear(135deg, teal.600 0%, green.700 100%)"
  );
  const textColor = useColorModeValue("gray.700", "gray.200");

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await revalidator.revalidate();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const totalServices = services?.reduce(
    (total, unit) => total + (unit.service_records?.length || 0),
    0
  ) || 0;

  return (
    <Box minH="100vh" bgGradient={bgGradient} p={{ base: 2, sm: 4 }}>
      {/* Mobile Header */}
      <Box
        bgGradient={headerBg}
        borderRadius="xl"
        p={{ base: 4, sm: 6 }}
        mb={{ base: 4, sm: 6 }}
        shadow="lg"
        position="sticky"
        top={{ base: 2, sm: 4 }}
        zIndex={10}
        mx={{ base: 0, sm: "auto" }}
        maxW="100%"
      >
        <VStack spacing={4}>
          <HStack justify="space-between" width="full">
            <HStack spacing={3} flex={1} minW={0}>
              <Icon as={FiZap} color="white" boxSize={{ base: 5, sm: 6 }} />
              <VStack align="start" spacing={0} flex={1} minW={0}>
                <Text
                  fontSize={{ base: "lg", sm: "xl" }}
                  fontWeight="bold"
                  color="white"
                  letterSpacing="tight"
                  noOfLines={1}
                >
                  Servis Yönetimi
                </Text>
                <Text fontSize={{ base: "xs", sm: "sm" }} color="whiteAlpha.800" noOfLines={1}>
                  {services?.length || 0} Ünite • {totalServices} Servis
                </Text>
              </VStack>
            </HStack>
            <IconButton
              aria-label="Yenile"
              icon={isRefreshing ? <Spinner size="sm" /> : <FiRefreshCw />}
              onClick={handleRefresh}
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              size={{ base: "md", sm: "lg" }}
              isLoading={isRefreshing}
              flexShrink={0}
            />
          </HStack>
        </VStack>
      </Box>

      {/* Content */}
      <Box pb={{ base: 4, sm: 6 }} px={{ base: 0, sm: 0 }}>
        {services && services.length > 0 ? (
          <VStack spacing={{ base: 3, sm: 4 }} align="stretch">
            {services.map((unit) => (
              <MobileUnitCard
                key={`${unit.project_name}-${unit.apt_num}`}
                unit={unit}
              />
            ))}
          </VStack>
        ) : (
          <Center py={20}>
            <VStack spacing={4}>
              <Icon as={FiTool} boxSize={16} color="gray.300" />
              <VStack spacing={2}>
                <Text fontSize="lg" color="gray.500" fontWeight="medium">
                  Henüz servis kaydı bulunmuyor
                </Text>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  Yeni iş emirleri burada görünecektir
                </Text>
              </VStack>
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={handleRefresh}
                variant="outline"
                colorScheme="teal"
                size="sm"
                isLoading={isRefreshing}
              >
                Yenile
              </Button>
            </VStack>
          </Center>
        )}
      </Box>
    </Box>
  );
}